import { useMemo, useState } from 'react';
import { formatEngineSizeForDisplay } from '../../utils/engineSize';
import ReorderableImageGrid from '../ReorderableImageGrid';
import { prepareImages } from '../../utils/compressImage';

function AddVehicleSection({
  message,
  handleUpload,
  uploadForm,
  handleUploadInputChange,
  manufacturerOptions,
  addManufacturerSubmitting,
  onAddManufacturer,
  photoPickerKey,
  setUploadImages,
  setPhotoPickerKey,
  uploadImages,
  imagePreviews,
}) {
  const [isMakeFocused, setIsMakeFocused] = useState(false);
  const [pickerNotice, setPickerNotice] = useState('');
  const makeValue = String(uploadForm.make || '');
  const normalizedMakeValue = makeValue.trim().toLowerCase();

  const filteredManufacturers = useMemo(() => {
    const all = Array.isArray(manufacturerOptions) ? manufacturerOptions : [];
    if (!normalizedMakeValue) {
      return all.slice(0, 120);
    }

    return all
      .filter((name) => String(name).toLowerCase().includes(normalizedMakeValue))
      .slice(0, 120);
  }, [manufacturerOptions, normalizedMakeValue]);

  const hasExactManufacturerMatch =
    normalizedMakeValue.length > 0 &&
    Array.isArray(manufacturerOptions) &&
    manufacturerOptions.some((name) => String(name).trim().toLowerCase() === normalizedMakeValue);

  const showMakeDropdown = isMakeFocused && filteredManufacturers.length > 0;
  const engineSizeDisplayValue = formatEngineSizeForDisplay(uploadForm.engineSize);

  const selectMake = (name) => {
    handleUploadInputChange({
      target: {
        name: 'make',
        value: name,
      },
    });
    setIsMakeFocused(false);
  };

  return (
    <section className="add-vehicle-block">
      <div className="section-header">
        <h2>Add New Vehicle</h2>
      </div>

      {message && <p className="status-text">{message}</p>}

      <form className="upload-form" onSubmit={handleUpload}>
        <label htmlFor="vehicle-make-list" className="make-list-label">Make</label>
        <div className="make-search-wrap">
          <input
            id="vehicle-make-list"
            className="make-search-input"
            type="text"
            name="make"
            placeholder="Start typing manufacturer"
            value={uploadForm.make}
            onChange={handleUploadInputChange}
            onFocus={() => setIsMakeFocused(true)}
            onBlur={() => {
              setTimeout(() => setIsMakeFocused(false), 120);
            }}
            autoComplete="off"
            required
          />

          {showMakeDropdown && (
            <div className="make-search-dropdown" role="listbox" aria-label="Manufacturer suggestions">
              {filteredManufacturers.map((name) => (
                <button
                  key={name}
                  type="button"
                  className="make-search-option"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    selectMake(name);
                  }}
                >
                  {name}
                </button>
              ))}
            </div>
          )}
        </div>

        {normalizedMakeValue && !hasExactManufacturerMatch && (
          <button
            type="button"
            className="make-picker-add-btn"
            onClick={() => onAddManufacturer(makeValue)}
            disabled={addManufacturerSubmitting}
          >
            {addManufacturerSubmitting
              ? 'Adding Manufacturer...'
              : `Add "${makeValue.trim()}" to manufacturers`}
          </button>
        )}
        <input
          type="text"
          name="model"
          placeholder="Model"
          value={uploadForm.model}
          onChange={handleUploadInputChange}
          required
        />
        <input
          type="number"
          name="year"
          placeholder="Year"
          value={uploadForm.year}
          onChange={handleUploadInputChange}
          required
        />
        <input
          type="number"
          name="mileage"
          placeholder="Mileage"
          value={uploadForm.mileage}
          onChange={handleUploadInputChange}
          required
        />
        <select
          name="vehicleType"
          value={uploadForm.vehicleType}
          onChange={handleUploadInputChange}
          required
        >
          <option value="">Vehicle type</option>
          <option value="car">Car</option>
          <option value="van">Van</option>
          <option value="pickup">Pickup</option>
          <option value="motorhome">Motorhome</option>
          <option value="other">Other</option>
        </select>
        <select
          name="fuelType"
          value={uploadForm.fuelType}
          onChange={handleUploadInputChange}
          required
        >
          <option value="">Fuel type</option>
          <option value="petrol">Petrol</option>
          <option value="diesel">Diesel</option>
          <option value="electric">Electric</option>
        </select>
        <select
          name="gearboxType"
          value={uploadForm.gearboxType}
          onChange={handleUploadInputChange}
          required
        >
          <option value="">Gearbox</option>
          <option value="manual">Manual</option>
          <option value="automatic">Automatic</option>
        </select>
        <input
          type="text"
          name="engineSize"
          placeholder="Engine size (e.g. 1598 cc)"
          value={uploadForm.engineSize}
          onChange={handleUploadInputChange}
          required
        />
        {String(uploadForm.engineSize || '').trim().length > 0 && (
          <p className="status-text">Displays as: {engineSizeDisplayValue}</p>
        )}
        <input
          type="text"
          name="registrationNumber"
          placeholder="Registration number"
          value={uploadForm.registrationNumber}
          onChange={handleUploadInputChange}
          required
        />
        <input
          type="number"
          name="salePrice"
          placeholder="Sale price (£)"
          value={uploadForm.salePrice}
          onChange={handleUploadInputChange}
          required
        />
        <input
          type="number"
          name="financePrice"
          placeholder="Finance monthly (£)"
          value={uploadForm.financePrice}
          readOnly
        />
        <textarea
          name="description"
          placeholder="Vehicle description"
          value={uploadForm.description}
          onChange={handleUploadInputChange}
          rows="4"
          required
        />
        <input
          key={photoPickerKey}
          type="file"
          accept="image/*"
          onChange={async (event) => {
            const files = Array.from(event.target.files || []);
            if (files.length > 0) {
              setPhotoPickerKey((current) => current + 1);
              setPickerNotice('Preparing photos...');
              const { ready, failed } = await prepareImages(files);
              setUploadImages((current) => [...current, ...ready].slice(0, 20));
              setPickerNotice(failed > 0 ? `${failed} photo(s) could not be read and were skipped.` : '');
            }
          }}
          multiple
        />
        {pickerNotice && <p className="status-text">{pickerNotice}</p>}
        <p className="status-text">Up to 20 photos at a time ({uploadImages.length}/20 added). You can add more later by editing the vehicle.</p>
        <button
          type="button"
          className="ghost-btn"
          onClick={() => {
            setUploadImages([]);
            setPhotoPickerKey((current) => current + 1);
          }}
          disabled={uploadImages.length === 0}
        >
          Clear Photos
        </button>
        {imagePreviews.length > 0 && (
          <ReorderableImageGrid
            items={imagePreviews.map((image) => ({ id: image.url, url: image.url, alt: image.name }))}
            onMove={(from, to) =>
              setUploadImages((current) => {
                if (to < 0 || to >= current.length) return current;
                const next = [...current];
                const [moved] = next.splice(from, 1);
                next.splice(to, 0, moved);
                return next;
              })
            }
            onRemove={(index) => setUploadImages((current) => current.filter((_, i) => i !== index))}
          />
        )}
        <button type="submit">Upload Vehicle</button>
      </form>
    </section>
  );
}

export default AddVehicleSection;
